<template>
  <div id="pricing-table-compare-container">
    <div>
      <div v-if="!discount" class="toggle-container">
        <button :class="{ active: billingCycle === 'yearly' }" @click="setBillingCycle('yearly')">
          <div class="toggle-discount">
            <span>{{ $t('pt_yearly') }}</span>
            <span class="discount-box">{{ $t('pt_discount_pct') }}</span>
          </div>
        </button>
        <button :class="{ active: billingCycle === 'monthly' }" @click="setBillingCycle('monthly')">{{ $t('pt_monthly') }}</button>
      </div>

      <div id="pricing-table-compare" class="pricing-table-compare">
        <table id="pricing-table">
          <thead id="table-header">
            <tr id="table-header-row">
              <th class="head_title">{{ $t('pt_compare_try') }}</th>
              <th v-for="(plan) in plans" :key="plan.name" :class="[plan.class, { 'top-plan-header': plan.top }]">
                <div v-if="plan.top" class="top_badge">{{ $t('pt_popular') }}</div>
                <h2>{{ plan.name }}</h2>
                <div class="plan_price_wrapper">
                  <transition name="fade-price" mode="out-in">
                    <div :key="billingCycle" class="plan_price">
                      <span v-if="discount">
                        <span class="discount_price">
                          {{
                            ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice) %
                              1).toFixed(2).substring(2)
                              !== '00'
                              ? ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice)).toFixed(2)
                              : ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice))
                          }}{{ $currency.symbol }}
                        </span>
                        {{
                          ((billingCycle === 'yearly' ? plan.yearlyPriceDiscount : plan.monthlyPriceDiscount) %
                            1).toFixed(2).substring(2)
                            !== '00'
                            ? ((billingCycle === 'yearly' ? plan.yearlyPriceDiscount :
                              plan.monthlyPriceDiscount)).toFixed(2)
                            : ((billingCycle === 'yearly' ? plan.yearlyPriceDiscount : plan.monthlyPriceDiscount))
                        }}{{ $currency.symbol }}
                        <span class="plan_monthly_text">{{ $t('pt_per_month') }}</span>
                      </span>
                      <span v-else> {{
                        ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice) % 1).toFixed(2).substring(2)
                          !== '00'
                          ? ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice)).toFixed(2)
                          : ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice))
                      }}{{ $currency.symbol }}<span class="plan_monthly_text">{{ $t('pt_per_month') }}</span></span>
                    </div>
                  </transition>
                </div>

                <a @click="createCheckoutSession(plan)" class="table_plan_btn">
                  <div v-if="!isPlanActive(plan)" class="arrow_right">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <path
                        d="M19.25 10.9999H2.75M12.8333 4.58325L19.25 10.9999L12.8333 4.58325ZM19.25 10.9999L12.8333 17.4166L19.25 10.9999Z"
                        stroke="#F93C34" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </div>
                  <span v-if="!isPlanActive(plan)" style="text-transform: uppercase;">
                      {{ plan.buttonText }}
                  </span>
                  <span v-else style="text-transform: uppercase;">
                      {{ $t('pt_active_plan') }}
                  </span>
                </a>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(feature, index) in features" :key="index">

              <td :class="['feature-label', { 'highlight': feature.highlight, 'disabled': feature.disabled }]">
                <div v-if="feature.tooltipText" class="has-tooltip" data-balloon-length="large"
                  :aria-label="feature.tooltipText" data-balloon-pos="up">
                  <span>
                    {{ feature.label }}</span>
                </div>
                <div v-else>
                  {{ feature.label }}
                </div>
              </td>



              <td v-for="(plan) in plans" :key="plan.name" :class="[
                { 'top-plan-body': plan.top },
                { 'top-plan-last': plan.top && index === features.length - 1 }
              ]">
                <span v-if="isFeatureEnabled(plan.features[feature.key])">
                  <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.14575 7L5.13558 11L15.1102 1" stroke="#15DE41" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </span>
                <span v-else-if="plan.features[feature.key] === false"></span>
                <span v-else>{{ plan.features[feature.key] }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from "@/eventBus";

export default {
  props: {
    activePlan: {
        type: String,
        default: 'Zadarmo',
    },
    paymentType: {
        type: String,
        default: "monthly",
    },
    is_paid: {
        type: Number,
        default: 0,
    },
    discount: {
      type: Boolean,
      default: false,
    },
    is_logged_in: {
      type: Boolean | Number,
      default: false,
    },
    user_id: {
      type: Number,
      default: null,
    },
    register_status: {
      type: Boolean | Number,
      default: false,
    },
  },
  data() {
    // Cennik ceny zo single source (plan_prices cez window.__PRICING__), s fallbackom
    // na povodne hodnoty (aby pri chybajucej injekcii ostalo spravanie 1:1).
    const __P = (typeof window !== 'undefined' && window.__PRICING__) || {};
    const price = (key, period, fallback) => (__P[key] && __P[key][period] != null) ? Number(__P[key][period]) : fallback;
    return {
      billingCycle: 'yearly',
      plans: [
        {
          name: this.$t('pt_cmp_plan_free'),
          monthlyPrice: price('free', 'month', 0), yearlyPrice: price('free', 'year', 0),
          yearlyPrice: price('free', 'year', 0), yearlyPriceDiscount: 0,
          monthID: 10, yearID: 10,
          class: "free-plan",
          buttonText: this.is_paid == 1 ? this.$t('pt_btn_choose') : this.$t('pt_btn_start_now'),
          features: {
            crm: true, max_clients: "max 3", max_offers: "max 3", online_offer: true, online_accept_decline_offer: true, track_opened: true, send_offers: true, discount_policy: false,
            images: true, hide_logo: false, custom_fonts: false, success_offers: true, contact_sources: false, sales_success: false,
            multi_company: false, team_accounts: false, download_docs: true, reviews: true, superfaktura: true, support: this.$t('pt_support_email'), item_database: true, export_pdf: true, api_connect: false, ai_assistant: false, margin_calculation: false
          },
          top: false
        },
        {
          name: this.$t('pt_cmp_plan_profi'),
          monthlyPrice: price('profi', 'month', 9.99), yearlyPrice: price('profi', 'year', 7.99),
          yearlyPriceDiscount: (price('profi', 'year', 7.99) * 0.5),
          monthID: 14, yearID: 13,
          class: "pro-plan",
          buttonText: this.is_paid == 1 ? this.$t('pt_btn_choose') : this.$t('pt_btn_start_now'),
          features: {
            crm: true, max_clients: this.$t('pt_unlimited'), max_offers: this.$t('pt_unlimited'), online_offer: true, online_accept_decline_offer: true, track_opened: true, send_offers: true, discount_policy: false,
            images: true, hide_logo: true, custom_fonts: true, success_offers: true, contact_sources: true, sales_success: true,
            multi_company: true, team_accounts: false, download_docs: true, reviews: true, superfaktura: true, support: this.$t('pt_support_email'), item_database: true, export_pdf: true, api_connect: true, ai_assistant: false, margin_calculation: false
          },
          top: false

        },
        {
          name: this.$t('pt_cmp_plan_small'),
          monthlyPrice: price('small', 'month', 19.99), yearlyPrice: price('small', 'year', 15.99),
          yearlyPriceDiscount: (price('small', 'year', 15.99) * 0.5),
          monthID: 16, yearID: 15,
          class: "small-team-plan",
          buttonText: this.is_paid == 1 ? this.$t('pt_btn_choose') : this.$t('pt_btn_start_now'),
          features: {
            crm: true, max_clients: this.$t('pt_unlimited'), max_offers: this.$t('pt_unlimited'), online_offer: true, online_accept_decline_offer: true, track_opened: true, send_offers: true, discount_policy: true,
            images: true, hide_logo: true, custom_fonts: true, success_offers: true, contact_sources: true, sales_success: true,
            multi_company: true, team_accounts: "5", download_docs: true, reviews: true, superfaktura: true, support: this.$t('pt_support_chat'), item_database: true, export_pdf: true, api_connect: true, ai_assistant: true, margin_calculation: true
          },
          top: true

        },
        {
          name: this.$t('pt_cmp_plan_large'),
          monthlyPrice: price('large', 'month', 29.95), yearlyPrice: price('large', 'year', 23.96),
          yearlyPriceDiscount: (price('large', 'year', 23.96) * 0.5),
          monthID: 18, yearID: 17,
          class: "large-team-plan",
          buttonText: this.is_paid == 1 ? this.$t('pt_btn_choose') : this.$t('pt_btn_start_now'),
          features: {
            crm: true, max_clients: this.$t('pt_unlimited'), max_offers: this.$t('pt_unlimited'), online_offer: true, online_accept_decline_offer: true, track_opened: true, send_offers: true, discount_policy: true, api_connect: true, ai_assistant: true,
            images: true, hide_logo: true, custom_fonts: true, success_offers: true, contact_sources: true, sales_success: true,
            multi_company: true, team_accounts: "20", download_docs: true, reviews: true, superfaktura: true, support: this.$t('pt_support_priority'), item_database: true, export_pdf: true, margin_calculation: true
          },
          top: false

        }
      ],
      features: [
        { key: "client_management", label: this.$t('pt_feat_client_mgmt'), highlight: true },
        { key: "crm", label: this.$t('pt_feat_crm'), highlight: false },
        { key: "max_clients", label: this.$t('pt_feat_client_count'), highlight: false },
        { key: "offers_managment", label: this.$t('pt_feat_offers_mgmt'), highlight: true },
        { key: "item_database", label: this.$t('pt_feat_item_db'), highlight: false, tooltipText: this.$t('pt_feat_item_db_tooltip') },
        { key: "export_pdf", label: this.$t('pt_feat_export_pdf'), highlight: false },
        { key: "max_offers", label: this.$t('pt_feat_offers_count'), highlight: false },
        { key: "online_offer", label: this.$t('pt_feat_online_offer'), highlight: false, tooltipText: this.$t('pt_feat_online_offer_tooltip') },
        { key: "online_accept_decline_offer", label: this.$t('pt_feat_accept_decline'), highlight: false, tooltipText: this.$t('pt_feat_accept_decline_tooltip') },
        { key: "track_opened", label: this.$t('pt_feat_track_opened'), highlight: false },
        { key: "send_offers", label: this.$t('pt_feat_send_offers'), highlight: false },
        { key: "discount_policy", label: this.$t('pt_feat_discount_policy'), highlight: false, disabled: true },
        { key: "design", label: this.$t('pt_feat_design'), highlight: true },
        { key: "images", label: this.$t('pt_feat_images'), highlight: false },
        { key: "hide_logo", label: this.$t('pt_feat_hide_logo'), highlight: false },
        { key: "custom_fonts", label: this.$t('pt_feat_custom_colors'), highlight: false, tooltipText: this.$t('pt_feat_custom_fonts_tooltip') },
        { key: "statistics", label: this.$t('pt_feat_statistics'), highlight: true },
        { key: "success_offers", label: this.$t('pt_feat_offers_success'), highlight: false, tooltipText: this.$t('pt_feat_offers_success_tooltip') },
        { key: "contact_sources", label: this.$t('pt_feat_contact_sources'), highlight: false, tooltipText: this.$t('pt_feat_contact_sources_tooltip') },
        { key: "sales_success", label: this.$t('pt_feat_sales_success'), highlight: false, tooltipText: this.$t('pt_feat_sales_success_tooltip') },
        { key: "accounts", label: this.$t('pt_feat_accounts'), highlight: true },
        { key: "multi_company", label: this.$t('pt_feat_multi_company'), highlight: false, tooltipText: this.$t('pt_feat_multi_company_tooltip') },
        { key: "team_accounts", label: this.$t('pt_feat_team_accounts_label'), highlight: false, tooltipText: this.$t('pt_feat_team_accounts_tooltip') },
        { key: "advanced_functions", label: this.$t('pt_feat_advanced'), highlight: true },
        { key: "download_docs", label: this.$t('pt_feat_download_docs'), highlight: false },
        { key: "reviews", label: this.$t('pt_feat_reviews'), highlight: false },
        { key: "superfaktura", label: this.$t('pt_feat_superfaktura'), highlight: false },
        { key: "api_connect", label: this.$t('pt_feat_ai_desc_label'), highlight: false, tooltipText: this.$t('pt_feat_ai_desc_tooltip') },
        { key: "ai_assistant", label: this.$t('pt_feat_ai_offers_label'), highlight: false, disabled: true },
        { key: "margin_calculation", label: this.$t('pt_feat_margin_label'), highlight: false, disabled: true },
        { key: "clinet_support", label: this.$t('pt_feat_support_label'), highlight: true },
        { key: "support", label: this.$t('pt_feat_support'), highlight: false }
      ]
    };
  },
  created() {
    EventBus.$on("billingCycleChanged", this.updateBillingCycle);
  },
  mounted() {
    setTimeout(() => {
      if (window.location.hash === "#compare-features-list") {
        const compareFeaturesList = document.getElementById("pricing-table-compare-container");
        if (compareFeaturesList) {
          compareFeaturesList.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
        }
      }
    }, 100);
  },
  computed: {
    isPlanActive() {
      return (plan) => {
        return plan.name === this.activePlan.toUpperCase() && this.paymentType === this.billingCycle;
      }
    },
  },
  methods: {

    cancelSubscription() {
      if (this.subscription_status == 'Zrušené') {
        this.$toast.error(this.$t('pt_subscription_cancelled'));
        return;
      }
      axios.post("/api/stripe/customer-portal-cancel", {
        user_id: this.user_id,
      })
        .then((response) => {
          window.location.href = response.data.url;
        });
    },
    changePlan(priceId, data = null) {
      if(data){
        localStorage.setItem('plan', JSON.stringify(data));
      }
      window.location.href = "/admin/subscriptions/dashboard?invoice_details_alert=1";
      return;
      axios.post("/api/stripe/customer-portal-switch-plan", {
        price_id: priceId,
        user_id: this.user_id,
      })
        .then((response) => {
          window.location.href = response.data.url;
        });
    },
    createCheckoutSession(plan) {
      // is logged in
      let data = {
        plan: plan,
        billingCycle: this.billingCycle,
      }
      if (this.is_logged_in != 0) {
        let priceId = this.billingCycle == 'yearly' ? plan.yearID : plan.monthID;
        // is paid
        if (this.is_paid == 1) {
          // is free plan
          if (priceId == 10) {
            this.cancelSubscription();
          } else {
            // change plan
            this.changePlan(priceId, data);
          }
          return;
        }
        // is free plan and not paid
        if (priceId == 10) {
          window.location.href = "/admin";
          return;
        }
        if (this.register_status == 1) {
            axios
            .post("/api/stripe/create-checkout-session-without-payment", {
                discount: sessionStorage.getItem('digiuni_discount') && this.billingCycle == 'yearly',
                price_id: priceId,
                user_id: this.user_id,
            })
            .then((response) => {
                window.location.href = response.data.url;
            });
            return;
        }

        localStorage.setItem('plan', JSON.stringify(data));
        window.location.href = "/admin/subscriptions/dashboard?invoice_details_alert=1";
        return;
        axios
          .post("/api/stripe/create-checkout-session", {
            price_id: priceId,
            user_id: this.user_id,
            send_email: true,
          })
            .then((response) => {
                window.location.href = response.data.url;
            });
        return;
        // continue in registration
      }
      // if is not logged in store to session and start registration
      localStorage.setItem('plan', JSON.stringify(data));
      window.location.href = "/register";
    },

    isFeatureEnabled(value) {
      return value === true;
    },
    beforeDestroy() {
      EventBus.$off("billingCycleChanged", this.updateBillingCycle);
    },
    setBillingCycle(cycle) {
      EventBus.$emit("billingCycleChanged", cycle);
    },
    updateBillingCycle(cycle) {
      this.billingCycle = cycle;
    },
  }
};
</script>

<style scoped>
.pricing-table-compare {
  width: 100%;
  font-family: Arial, sans-serif;
  opacity: 0;
  transform: translateX(-100px);
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  background-color: #fff;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  margin-top: 10px;
  min-width: 1000px;
}

thead {
  background: white;
  box-shadow: 0px 30px 50px 0px rgba(0, 0, 15, 0.067);
}

thead tr {
  background: white;
  z-index: 9999;
  position: relative;
  box-shadow: 0px 30px 50px 0px rgba(0, 0, 15, 0.067);
}

thead th {
  box-shadow: 0px 30px 50px 0px rgba(0, 0, 15, 0.067);

}

thead th,
td {
  padding: 24px 25px 25px 25px;
  border-left: 1.5px solid #ddd;
  border-right: 1.5px solid #ddd;
  color: #212541;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
}

tbody th,
td {
  padding: 10px 15px;
  border-left: 1px solid #ddd;
  border-right: 1px solid #ddd;
  color: #212541;
  font-size: 14px;
  font-weight: 400;
}

th {
  font-size: 18px;
  font-weight: bold;
  color: #212541;
  position: relative;
  text-align: left;

}

th h2 {
  color: #F93C34;
  font-weight: 800;
  font-size: 20px;
  margin-bottom: 10px;
}

th .top_badge {
  background: #F93C34;
  color: white;
  border-radius: 17px;
  padding: 2px 6px;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;

}

th .plan_price_wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

th .plan_price {
  color: #F93C34;
  font-size: 20px;
  font-weight: 800;
}

.plan_price .discount_price {
  font-size: 14px;
  text-decoration: line-through;
  color: #6B6F8C;
  margin-right: 4px;
}

th .plan_monthly_text {
  color: #6B6F8C;
  font-weight: 500;
  font-size: 14px;
}

th.head_title {
  text-align: left;
  font-size: 22px;
  font-weight: 800;

}

tbody tr:nth-child(odd) {
  background-color: #F7F7F7;
}

tbody tr td {
  text-align: left;
}

.toggle-container {
  display: flex;
  justify-content: center;
  background-color: white;
  width: fit-content;
  margin: auto;
  border-radius: 12px;
  border: 1px solid #E9E9E9;
  margin-bottom: 48px;
  opacity: 0;
  transform: translate(-200px, 0);
}

.toggle-container .toggle-discount {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.toggle-container .discount-box {
  background-color: #34D399;
  border-radius: 30px;
  padding: 4px 5px 2px 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  line-height: 100%;
  color: white;
}

.toggle-container button {
  padding: 8px 33px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #212541;
  border-radius: 12px;
  transition: 0.3s;
  font-weight: 500;
}

.toggle-container button.active {
  background: linear-gradient(90deg,
      #F93C34 0%,
      #d73c78 47.28%,
      #c41f5f 75.15%);
  color: white;
  transform: scale(1.03);
}

[aria-label][data-balloon-pos] {
  position: static;
}

[aria-label][data-balloon-pos]:hover {
  position: relative;
}

[aria-label][data-balloon-pos]:before {
  display: none;
}

[aria-label][data-balloon-pos]:after {
  background: white;
  box-shadow: 0px 30px 50px 0px rgba(0, 0, 15, 0.067);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #212541;
  border: 1px solid #ECEDEF;
  z-index: 9999;
}

.feature-label {
  font-weight: 500;
  text-align: left;
  font-size: 14px;
  color: #6B6F8C;
}

.feature-label .has-tooltip span {
  text-decoration: underline;
  text-decoration-style: dashed;
  text-decoration-color: #D0D3E5;
  text-underline-offset: 5px;

}

.feature-label.highlight {
  font-weight: 800;
}

.feature-label.disabled {
  color: #BABABA
}

.fade-price-enter-active,
.fade-price-leave-active {
  transition: all 0.3s ease;

}

.fade-price-enter-from {
  opacity: 0;
}

.fade-price-leave-to {
  opacity: 0;
}

a.table_plan_btn {
  display: inline-flex;
  padding: 15px 20px 15px 20px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  flex-shrink: 0;
  border-radius: 58px;
  font-weight: 800;
  text-decoration: none;
  color: #F93C34;
  position: relative;
  background: linear-gradient(90deg,
      #f93c34 0%,
      #d73c78 47.28%,
      #c41f5f 75.15%);
  font-size: 14px;
}

a.table_plan_btn::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  background: white;
  border-radius: 55px;
  z-index: 0;
}

a.table_plan_btn span {
  position: relative;
  z-index: 1;
  transition: all 0.2s ease-in-out;
  color: #CE2E6C
}

a.table_plan_btn:hover span {
  transform: translate(-25px, 0);
  transition: all 0.2s ease-in-out;
  color: white;

}

.top a.table_plan_btn:hover {
  background: linear-gradient(90deg,
      #f93c34 0%,
      #d73c78 47.28%,
      #c41f5f 75.15%);
  color: white;
}

a.table_plan_btn:hover::before {
  background: transparent;
}

a.table_plan_btn .arrow_right {
  transition: all 0.2s ease-in-out;
  width: 23px;
  height: 23px;
  flex-shrink: 0;
  border-radius: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FAEFEF;
  transition: all 0.2s ease-in-out;
  transform: translateX(0);
  padding: 4px;

}


a.table_plan_btn:hover .arrow_right {
  transform: translateX(110px);
}

.top-plan-header {
  border-top: 2px solid #C52060 !important;
  border-left: 2px solid #C52060 !important;
  border-right: 2px solid #C52060 !important;
}

.top-plan-body {
  border-left: 2px solid #C52060 !important;
  border-right: 2px solid #C52060 !important;
}

.top-plan-last {
  border-bottom: 2px solid #C52060 !important;
}

@media (max-width: 1024px) {
  a.table_plan_btn {

    white-space: nowrap;
  }

  th,
  td {
    padding: 8px 23px;
  }
}
</style>
